import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader, Panel, LoadingState, ErrorState } from "@/components/common";
import { ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import { Unlock } from "@/pages/RealPortfolio";
import { SemanticsTable, ReportsTable, IssuesTable } from "@/components/validation/ValidationParts";

export default function TradeValidation() {
  const [grant, setGrant] = useState(null);
  const [ttl, setTtl] = useState(0);
  useEffect(() => {
    if (!grant) { return undefined; }
    const t = setTimeout(() => setGrant(null), ttl * 1000);
    return () => clearTimeout(t);
  }, [grant, ttl]);
  const lock = async () => {
    try {
      await api.post("/portfolio/real/lock", {}, { headers: { "X-Portfolio-Grant": grant } });
    } catch (e) {
      toast.error("Lock request failed; details hidden locally and expire within 10 min");
    } finally { setGrant(null); }
  };
  const { data: s, isLoading, isError, refetch } = useQuery({ queryKey: ["tv-summary"], gcTime: 0, queryFn: async () => (await api.get("/validation/summary")).data });
  return (
    <div data-testid="trade-validation-page" className="space-y-4">
      <PageHeader title="Trades & Positions Validation" subtitle="Advisory checks of Sharekhan replies — never changes your ledger, holdings, capital or positions, never logs in, syncs or trades" icon={ClipboardCheck} demo={false} testId="trade-validation-header" />
      {isLoading && <LoadingState label="Loading validation status…" />}
      {isError && <ErrorState label="Validation status could not be loaded" onRetry={refetch} />}
      {s && (
        <>
          <div data-testid="tv-flags" className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-400">
            <span data-testid="tv-positions-verified">Positions: <b className="text-amber-300">{s.positions_verified ? "VERIFIED" : "UNVERIFIED"}</b></span>
            <span data-testid="tv-nonempty">Real non-empty positions replies seen: {s.real_nonempty_positions_reports}</span>
            <span data-testid="tv-advisory">Advisory only · Orders possible: no</span>
          </div>
          <Panel title="What each Sharekhan reply means" testId="tv-semantics-panel"><div className="p-4"><SemanticsTable s={s} /></div></Panel>
          <Panel title="Validation results" testId="tv-reports-panel"><div className="p-4"><ReportsTable /></div></Panel>
          <Panel title="Issue details" testId="tv-issues-panel" actions={grant && <button data-testid="tv-lock" onClick={lock} className="text-xs text-slate-300 underline underline-offset-2 hover:text-white">Lock details</button>}>
            <div className="p-4">{grant ? <IssuesTable grant={grant} severities={s.severities} /> : <Unlock onGrant={(g, t) => { setTtl(t); setGrant(g); }} />}</div>
          </Panel>
        </>
      )}
    </div>
  );
}
